import { useState } from 'react';
import { Check, Loader2, PlusCircle, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useCreateContentBlock,
  usePlaceScope,
  useContentBlocks,
  useSetContentBlockStatus,
} from '@/hooks/useKnowledgeBase';
import type { ContentKind, ContentStatus } from '@/api/kbResolvedStory';

/** Pull the backend's `detail` off an axios error, falling back to a generic message. */
function errMsg(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return (e as Error)?.message ?? 'Something went wrong';
}

const KINDS: ContentKind[] = ['narrative', 'qa_answer', 'table_note'];
const STATUSES: ContentStatus[] = ['draft', 'approved', 'stale', 'retired'];

const selectCls =
  'h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-ring';

export interface ContentBlockAuthoringProps {
  /** The node currently selected in the spine tree — prefills the placement target. */
  selectedNodeId?: number | null;
  className?: string;
}

/**
 * Authoring panel (Wellington #2 Phase-1): write a content block ONCE, then place it on a
 * product-spine node so the inheritance engine flows it to descendants. After either action
 * the resolved-story query is invalidated, so the tree on the left re-resolves and the new
 * "own" / "inherited" provenance appears live — the single-source story, made authorable.
 */
export function ContentBlockAuthoring({ selectedNodeId, className }: ContentBlockAuthoringProps) {
  const create = useCreateContentBlock();
  const place = usePlaceScope();
  const library = useContentBlocks();
  const statusMutation = useSetContentBlockStatus();

  // Create-block form
  const [blockKey, setBlockKey] = useState('');
  const [title, setTitle] = useState('');
  const [contentKind, setContentKind] = useState<ContentKind>('narrative');
  const [status, setStatus] = useState<ContentStatus>('approved');
  const [bodyText, setBodyText] = useState('');

  // Place form (block_key reuses the create form's key by default — the just-authored block)
  const [questionId, setQuestionId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [flows, setFlows] = useState(true);

  const effectiveNodeId = nodeId !== '' ? Number(nodeId) : selectedNodeId ?? undefined;

  const submitCreate = () => {
    create.mutate(
      { block_key: blockKey.trim(), title: title.trim(), content_kind: contentKind, body_text: bodyText, status },
      { onSuccess: () => setBodyText('') }
    );
  };

  const submitPlace = () => {
    if (effectiveNodeId === undefined || questionId === '') return;
    place.mutate({
      block_key: blockKey.trim(),
      canonical_question_id: Number(questionId),
      spine_node_id: effectiveNodeId,
      flows_to_descendants: flows,
    });
  };

  return (
    <Card className={cn('border-dashed', className)}>
      <CardContent className="p-4 space-y-5">
        <div className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Author a content block</span>
          <Badge variant="outline" className="text-[10px] py-0 px-1 font-normal">
            write once · inherits everywhere
          </Badge>
        </div>

        {/* Create block */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input placeholder="block_key (e.g. china.buy_sell)" value={blockKey}
            onChange={(e) => setBlockKey(e.target.value)} />
          <Input placeholder="title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <select className={selectCls} value={contentKind}
            onChange={(e) => setContentKind(e.target.value as ContentKind)} aria-label="content kind">
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select className={selectCls} value={status}
            onChange={(e) => setStatus(e.target.value as ContentStatus)} aria-label="status">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Textarea placeholder="Block body — the prose to reuse across strategies." rows={3}
          value={bodyText} onChange={(e) => setBodyText(e.target.value)} className="text-sm" />
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={submitCreate}
            disabled={create.isPending || !blockKey.trim() || !title.trim() || !bodyText.trim()}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />}
            Create block
          </Button>
          {create.isSuccess && (
            <span className="text-xs text-green-700 flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Created “{create.data.block_key}” (v{create.data.version}, {create.data.status})
            </span>
          )}
          {create.isError && <span className="text-xs text-red-600">{errMsg(create.error)}</span>}
        </div>

        {/* Place block */}
        <div className="border-t pt-4 space-y-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Place it on the spine</span>
            <span className="text-xs text-muted-foreground">
              {effectiveNodeId !== undefined ? `node ${effectiveNodeId}` : 'select a level on the left'}
            </span>
          </div>

          {library.data && library.data.blocks.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {library.data.blocks.map((b) => (
                <span
                  key={b.content_block_id}
                  className={cn(
                    'inline-flex items-center rounded-full border text-xs transition-colors',
                    blockKey === b.block_key
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'text-muted-foreground'
                  )}
                  title={`${b.content_kind} · ${b.status} · v${b.version}`}
                >
                  <button
                    type="button"
                    onClick={() => setBlockKey(b.block_key)}
                    className="px-2 py-0.5 hover:opacity-80"
                  >
                    {b.block_key}
                    {b.status !== 'approved' && <span className="ml-1 opacity-70">({b.status})</span>}
                  </button>
                  {b.status === 'draft' && (
                    <button
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={() =>
                        statusMutation.mutate({ blockKey: b.block_key, status: 'approved' })
                      }
                      className="border-l px-1.5 py-0.5 text-green-700 hover:bg-green-50 disabled:opacity-50"
                      title="Approve so it can be placed"
                    >
                      approve
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {statusMutation.isError && (
            <span className="text-xs text-red-600">{errMsg(statusMutation.error)}</span>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input placeholder="block_key" value={blockKey} onChange={(e) => setBlockKey(e.target.value)} />
            <Input placeholder="canonical_question_id" inputMode="numeric" value={questionId}
              onChange={(e) => setQuestionId(e.target.value.replace(/[^0-9]/g, ''))} />
            <Input placeholder={`spine_node_id${selectedNodeId != null ? ` (${selectedNodeId})` : ''}`}
              inputMode="numeric" value={nodeId}
              onChange={(e) => setNodeId(e.target.value.replace(/[^0-9]/g, ''))} />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={flows} onChange={(e) => setFlows(e.target.checked)} />
            Inherits to descendants (uncheck to apply only at this level)
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={submitPlace}
              disabled={place.isPending || effectiveNodeId === undefined || questionId === '' || !blockKey.trim()}>
              {place.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}
              Place block
            </Button>
            {place.isSuccess && (
              <span className="text-xs text-green-700 flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Placed on node {place.data.spine_node_id}
                {place.data.flows_to_descendants ? ' — inherits down' : ''}
              </span>
            )}
            {place.isError && <span className="text-xs text-red-600">{errMsg(place.error)}</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
